import { useFetchImgObjUrl } from "@/hooks/useFetchImgObjUrl";
import {
  SelectedProduct as SelectedProductType,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { ActionIcon, Button } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { produce } from "immer";

interface Props {
  product: SelectedProductType;
  selectedProductIdx: number;
}

export const SelectedProduct = (props: Props) => {
  const { product, selectedProductIdx } = props;
  const img_id = product.id || "";
  const image_url = product.image_url || "";
  const description = product.description || "";

  const { data: imgObjUrlResp } = useFetchImgObjUrl({ img_id, image_url });
  const imgObjUrl = imgObjUrlResp || "";
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);

  const isReference = product.source === "reference";
  const isConfirmed =
    product.source === "reference" && product.confirmed === true;

  // Open the confirm chooser for THIS product's detection. When `clear` is set
  // (the "Change" action) we first drop the existing confirmation so the chooser
  // opens neutral — no remembered/pre-checked pick.
  const openConfirm = (clear: boolean) => {
    if (product.source !== "reference") return;
    const {
      idxToFeEmptyArea,
      setIdxToFeEmptyArea,
      activeEmptyAreaIdx,
      setConfirming,
      recordEvent,
    } = useCoverageBetaStore.getState();

    if (clear) {
      recordEvent("confirm_reopen");
      setIdxToFeEmptyArea(
        produce(idxToFeEmptyArea, (draft) => {
          const area = draft[activeEmptyAreaIdx];
          if (!area) return;
          const p = area.selectedProducts[selectedProductIdx];
          if (p && p.source === "reference") p.confirmed = false;
          if (area.confirmedProductId === product.id) {
            area.confirmedProductId = null;
          }
        }),
      );
    }

    setConfirming({
      emptyAreaIdx: activeEmptyAreaIdx,
      referenceImgIdx: product.referenceImgIdx,
      referenceProductDetectionIdx: product.referenceProductDetectionIdx,
    });
  };

  const remove = () => {
    const {
      idxToFeEmptyArea,
      setIdxToFeEmptyArea,
      activeEmptyAreaIdx,
      recordEvent,
    } = useCoverageBetaStore.getState();
    recordEvent("product_removed");
    setIdxToFeEmptyArea(
      produce(idxToFeEmptyArea, (draft) => {
        const area = draft[activeEmptyAreaIdx];
        if (!area) return;
        const removed = area.selectedProducts[selectedProductIdx];
        area.selectedProducts.splice(selectedProductIdx, 1);
        // Removing a product invalidates a confirmation that pointed at it.
        if (removed && area.confirmedProductId === removed.id) {
          area.confirmedProductId = null;
        }
        // An emptied area goes back to UNRESOLVED (so the labeler can pick a new
        // product or explicitly choose "No match") — not the grey no-match state.
        if (area.selectedProducts.length === 0) {
          area.isValid = false;
          area.confirmedProductId = null;
        }
      }),
    );
  };

  return (
    <div className="group flex gap-2 items-center hover:bg-gray-100">
      <div className="size-12 border p-1 rounded shrink-0">
        <div className="flex bg-gray-200 justify-center rounded">
          <div className="h-10">
            {!!imgObjUrl && (
              <img src={imgObjUrl} className="object-contain size-full" />
            )}
          </div>
        </div>
      </div>

      <div className="text-sm truncate min-w-0">{description}</div>

      {!isReadOnly && (
        <div className="ml-auto flex items-center gap-1 pr-2 shrink-0">
          {isReference &&
            (isConfirmed ? (
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={() => openConfirm(true)}
                title="Pick a different product for this detection"
              >
                Change
              </Button>
            ) : (
              <Button
                size="compact-xs"
                variant="light"
                color="blue"
                onClick={() => openConfirm(false)}
                title="Confirm this product"
              >
                Confirm
              </Button>
            ))}
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={remove}
            title="Remove this product"
          >
            <IconX size={16} />
          </ActionIcon>
        </div>
      )}
    </div>
  );
};
